/**
 * NOS-266 builder UI kit: the floating-panel chrome that was re-declared
 * inline across ~12 sites. Geometry (top/left/right/bottom/width/maxHeight/
 * zIndex) stays per-panel via `position`; everything visual lives here once.
 *
 * fontFamily is set here ON PURPOSE even though `.builder-root` also sets
 * it: subtrees rendered through createPortal (e.g. BlogsTab's category
 * dropdown) escape the builder root, so the chrome must carry its own font.
 */
import type { CSSProperties } from "react"
import { IconBtn } from "../IconBtn"

export const BUILDER_FONT = "'DM Sans', sans-serif"

export const PANEL_HEADER_BORDER = "1px solid rgba(255,255,255,0.06)"

export const PANEL_CHROME: CSSProperties = {
  position: "fixed",
  background: "rgba(12,12,16,0.97)",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: 14,
  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  fontFamily: BUILDER_FONT,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}

export interface FloatingPanelProps {
  /** Per-panel geometry: top/left/right/bottom/width/maxHeight/zIndex. */
  position: CSSProperties
  /** Renders the standard header (accent dot + title + close) when set. */
  title?: React.ReactNode
  /** Header accent dot color (usually the org primary color). */
  dotColor?: string
  onClose?: () => void
  className?: string
  "data-testid"?: string
  children: React.ReactNode
}

export function FloatingPanel({
  position,
  title,
  dotColor,
  onClose,
  className,
  "data-testid": testId,
  children,
}: FloatingPanelProps) {
  return (
    <div className={className} data-testid={testId} style={{ ...PANEL_CHROME, ...position }}>
      {title !== undefined && (
        <div
          style={{
            padding: "14px 16px",
            borderBottom: PANEL_HEADER_BORDER,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {dotColor && (
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor }} />
            )}
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{title}</span>
          </div>
          {onClose && (
            <IconBtn size={26} onClick={onClose}>
              ✕
            </IconBtn>
          )}
        </div>
      )}
      {children}
    </div>
  )
}
